import React from 'react';
import EventHeader from './event-detail/EventHeader';
import EventActions from './event-detail/EventActions';
import EventWhen from './event-detail/EventWhen';
import EventWhere from './event-detail/EventWhere';
import EventMeetCard from './event-detail/EventMeetCard';
import EventDescription from './event-detail/EventDescription';
import EventPeople from './event-detail/EventPeople';
import EventReminders from './event-detail/EventReminders';
import EventMetaFooter from './event-detail/EventMetaFooter';
import { useEventDetail } from '../../hooks/useCalendarEvents';
import type { CalendarEvent, CalendarEventDetail } from '../../types/integrations';

interface EventDetailViewProps {
  event: CalendarEvent;
  onEdit: () => void;
  onDelete: () => void;
}

const SkeletonRow: React.FC = () => (
  <div className="flex items-start gap-3 animate-pulse">
    <div className="w-5 h-5 rounded bg-surface-elevated shrink-0" />
    <div className="flex-1 space-y-1.5">
      <div className="h-3 w-3/4 rounded bg-surface-elevated" />
      <div className="h-3 w-1/3 rounded bg-surface-elevated" />
    </div>
  </div>
);

const EventDetailView: React.FC<EventDetailViewProps> = ({ event, onEdit, onDelete }) => {
  const { data: detail, isLoading } = useEventDetail(event.id, event.calendar_id);

  // Use detail when loaded, falling back to the list-level event for the header
  // and basic time/location info so the modal feels instant.
  const full: CalendarEventDetail = (detail ?? event) as CalendarEventDetail;
  const isTereikoAdHoc =
    !!full.is_tereiko && full.tereiko_resource_type === 'ad_hoc';

  return (
    <div className="space-y-5">
      <EventHeader event={full} />

      <EventActions
        meetingLink={full.meeting_link}
        htmlLink={full.html_link}
        canEdit={!!detail && isTereikoAdHoc}
        canDelete={!!detail && isTereikoAdHoc}
        onEdit={onEdit}
        onDelete={onDelete}
      />

      <div className="space-y-4 pt-1">
        <EventWhen event={full} />
        <EventWhere location={full.location} />
        {full.conference_data && <EventMeetCard data={full.conference_data} />}
        <EventDescription description={full.description} />

        {isLoading && !detail ? (
          <>
            <SkeletonRow />
            <SkeletonRow />
          </>
        ) : (
          <>
            <EventPeople
              organizer={full.organizer}
              creator={full.creator}
              attendees={full.attendees}
            />
            <EventReminders reminders={full.reminders} />
          </>
        )}
      </div>

      <EventMetaFooter createdAt={full.created_at} updatedAt={full.updated_at} />
    </div>
  );
};

export default EventDetailView;
